import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { List, Button, Card, Row, Col, message, Space, Tooltip, Spin, Empty, notification, Modal } from "antd";
import { AppstoreOutlined, BarsOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import MarketPlaceService from '../../../utils/api/marketplace';
import LayoutPage from '../../../layouts/Main';


const FilteredProducts = () => {
  const location = useLocation();
  const history = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [isGalleryView, setIsGalleryView] = useState(true);
  const [filterInfo, setFilterInfo] = useState({ category: '', subcategory: '' });
  const [addingToCart, setAddingToCart] = useState({});
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [pendingCartItem, setPendingCartItem] = useState(null);

  const user = JSON.parse(localStorage.getItem('user'));
  const role = localStorage.getItem('role');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const category = searchParams.get('category');
    const subcategory = searchParams.get('subcategory');

    setFilterInfo({ category, subcategory });
    loadFilteredProducts(category, subcategory);
  }, [location.search]);

  const loadFilteredProducts = async (category, subcategory) => {
    try {
      setLoading(true);
      const response = await MarketPlaceService.filterProducts(category, subcategory);

      if (response.data && response.data.success) {
        setProducts(response.data.products || []);
      } else {
        setProducts([]);
        message.warning('No se encontraron productos para los filtros seleccionados');
      }
    } catch (error) {
      console.error('Error loading filtered products:', error);
      message.error('Error al cargar los productos');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (product) => {
    history(`/marketplace/${product.category}/${product.sub_category?.[0] || 'general'}/${product.id}`);
  };

  const formatName = (name) => {
    if (!name) return '';
    return name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const addToCart = async (product, e) => {
    if (e) e.stopPropagation();

    if (!user) {
      const finalPrice = product.newPrice && product.newPrice !== product.price
        ? Number(product.newPrice)
        : Number(product.price) || 0;

      setPendingCartItem({
        id: product.id,
        product_id: product.id,
        supplier_id: product.supplier_id || 'default-supplier',
        name: product.name,
        category: product.category,
        sub_category: product.sub_category || product.subcategory || 'general',
        quantity: 1,
        price: finalPrice,
        imgs: product.images || [],
        description: product.description || ''
      });
      setIsLoginModalOpen(true);
      return;
    }

    if (addingToCart[product.id]) {
      return;
    }

    setAddingToCart(prev => ({ ...prev, [product.id]: true }));

    try {
      let cartResponse;
      try {
        cartResponse = await MarketPlaceService.getCartByClient(user.id);
      } catch (error) {
        cartResponse = { data: null };
      }

      let currentCart = cartResponse.data;
      let productsInCart = [];

      const finalPrice = product.newPrice && product.newPrice !== product.price
        ? Number(product.newPrice)
        : Number(product.price) || 0;

      if (currentCart && currentCart.products && Array.isArray(currentCart.products)) {
        productsInCart = [...currentCart.products];
        const existingProductIndex = productsInCart.findIndex(item =>
          item.id === product.id || item.product_id === product.id
        );

        if (existingProductIndex > -1) {
          productsInCart[existingProductIndex].quantity += 1;
          productsInCart[existingProductIndex].price = finalPrice;
        } else {
          const newProduct = {
            id: product.id,
            product_id: product.id,
            supplier_id: product.supplier_id || 'default-supplier',
            name: product.name,
            category: product.category,
            sub_category: product.sub_category || product.subcategory || 'general',
            quantity: 1,
            price: finalPrice,
            imgs: product.images || [],
            description: product.description || '',
            user_role: user.role,
            user_id: user.id
          };
          productsInCart.push(newProduct);
        }
      } else {
        const newProduct = {
          id: product.id,
          product_id: product.id,
          supplier_id: product.supplier_id || 'default-supplier',
          name: product.name,
          category: product.category,
          sub_category: product.sub_category || product.subcategory || 'general',
          quantity: 1,
          price: finalPrice,
          imgs: product.images || [],
          description: product.description || '',
          user_role: user.role,
          user_id: user.id
        };
        productsInCart = [newProduct];
      }

      const totalPrice = productsInCart.reduce((sum, item) =>
        sum + (Number(item.price) * Number(item.quantity)), 0
      );

      const cartData = {
        client_id: user.id,
        client_role: user.role,
        products: productsInCart,
        price: totalPrice,
        change_type: 'MX',
        status: 1
      };

      let result;
      if (currentCart && currentCart.id) {
        result = await MarketPlaceService.updateCart(cartData, currentCart.id);
      } else {
        result = await MarketPlaceService.addProductToCart(cartData);
      }

      const cartKey = `cart_${user.id}_${user.role}`;
      localStorage.setItem(cartKey, JSON.stringify(productsInCart));

      window.dispatchEvent(new Event('cartUpdated'));
      notification.success({
        description: `${product.name} se ha añadido al carrito correctamente`,
        placement: 'topRight',
        duration: 3,
      });

    } catch (error) {
      let errorMessage = 'No se pudo agregar el producto al carrito';

      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      } else if (error.request) {
        errorMessage = 'Error de conexión. Verifica tu internet.';
      }

      notification.error({
        description: errorMessage,
        placement: 'topRight',
        duration: 4,
      });
    } finally {
      setAddingToCart(prev => ({ ...prev, [product.id]: false }));
    }
  };

  const savePendingCartItem = () => {
    if (!pendingCartItem) return;
    const pendingKey = 'pending_cart_items';
    let pendingItems = [];

    try {
      const existing = localStorage.getItem(pendingKey);
      if (existing) {
        pendingItems = JSON.parse(existing);
      }
    } catch (error) {
      pendingItems = [];
    }

    const existingIndex = pendingItems.findIndex(item =>
      item.id === pendingCartItem.id || item.product_id === pendingCartItem.product_id
    );

    if (existingIndex > -1) {
      pendingItems[existingIndex].quantity += Number(pendingCartItem.quantity || 1);
      pendingItems[existingIndex].price = Number(pendingCartItem.price) || pendingItems[existingIndex].price || 0;
    } else {
      pendingItems.push(pendingCartItem);
    }

    localStorage.setItem(pendingKey, JSON.stringify(pendingItems));
    localStorage.setItem('post_login_redirect', '/marketplace/shopping-cart');
  };

  const handleLoginRedirect = () => {
    savePendingCartItem();
    history('/');
  };

  const handleRegisterRedirect = () => {
    savePendingCartItem();
    history('/register');
  };

  const renderLoginModal = () => (
    <Modal
      open={isLoginModalOpen}
      onCancel={() => setIsLoginModalOpen(false)}
      footer={null}
      centered
      closable
      width={420}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ background: '#0F5197', color: '#fff', padding: '28px 24px', textAlign: 'center' }}>
        <div style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.15)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 12
        }}>
          <ShoppingCartOutlined style={{ fontSize: 28, color: '#fff' }} />
        </div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
          !Inicia sesion para continuar!
        </h2>
        <p style={{ margin: '8px 0 0', fontSize: 14, opacity: 0.95 }}>
          Crea una cuenta o inicia sesion para agregar productos a tu carrito
        </p>
      </div>
      <div style={{ padding: '18px 24px 24px' }}>
        <Button
          type="primary"
          block
          size="large"
          onClick={handleRegisterRedirect}
          style={{ background: '#0F5197', borderColor: '#0F5197', fontWeight: 600 }}
        >
          Crear cuenta
        </Button>
        <Button
          block
          size="large"
          onClick={handleLoginRedirect}
          style={{ marginTop: 12, fontWeight: 600 }}
        >
          Ya tengo cuenta
        </Button>
        <div style={{ marginTop: 12, fontSize: 12, textAlign: 'center', color: '#8a8a8a' }}>
          Al continuar, aceptas nuestros terminos de servicio y politica de privacidad
        </div>
      </div>
    </Modal>
  );

  if (loading) {
    return (
      <LayoutPage>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Spin size="large" />
        </div>
      </LayoutPage>
    );
  }

  return (
    <LayoutPage>
      {renderLoginModal()}

      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
            Productos por categoría
          </h1>
          <span style={{ color: '#666', marginLeft: 'auto' }}>
            {products.length} producto{products.length !== 1 ? 's' : ''} encontrado{products.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Controles de vista */}
        <div style={{ marginBottom: 16, textAlign: "right" }}>
          <Space>
            <Tooltip title="Vista de galería">
              <Button
                icon={<AppstoreOutlined />}
                type={isGalleryView ? "primary" : "default"}
                onClick={() => setIsGalleryView(true)}
                style={{ minWidth: 80, height: 40, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 14 }}
              >
                Galería
              </Button>
            </Tooltip>
            <Tooltip title="Vista de lista">
              <Button
                icon={<BarsOutlined />}
                type={!isGalleryView ? "primary" : "default"}
                onClick={() => setIsGalleryView(false)}
                style={{ minWidth: 80, height: 40, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 14 }}
              >
                Lista
              </Button>
            </Tooltip>
          </Space>
        </div>

        {/* Lista de productos */}
        {products.length > 0 ? (
          <List
            grid={isGalleryView ? {
              gutter: 16,
              xs: 1,
              sm: 2,
              md: 3,
              lg: 4,
              xl: 4,
              xxl: 5
            } : undefined}
            dataSource={products}
            renderItem={(item) => {
              const price = Number(item.newPrice ?? item.price) || 0;
              const mainImage = item.images?.[0] || 'https://via.placeholder.com/200x200?text=Sin+Imagen';

              return (
                <List.Item>
                  {isGalleryView ? (
                    <Card
                      bordered={false}
                      style={{
                        borderRadius: 12,
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                        padding: '16px',
                        cursor: 'pointer',
                        width: '100%',
                        minHeight: '280px'
                      }}
                      bodyStyle={{
                        padding: 0,
                        height: '100%'
                      }}
                      hoverable
                      onClick={() => handleCardClick(item)}
                    >
                      {/* Imagen */}
                      <div style={{
                        height: '140px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: '12px'
                      }}>
                        <img
                          src={mainImage}
                          alt={item.name}
                          style={{
                            width: 'auto',
                            maxWidth: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: 8
                          }}
                        />
                      </div>

                      {/* Información del producto */}
                      <div style={{
                        minHeight: '100px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <div>
                          <h3 style={{
                            margin: '0 0 6px 0',
                            fontSize: '14px',
                            lineHeight: '1.4',
                            fontWeight: '500',
                            minHeight: '40px',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {item.name}
                          </h3>

                          {/* Categoría */}
                          <p style={{
                            color: "#00C897",
                            margin: '0 0 8px 0',
                            fontWeight: "600",
                            fontSize: '12px',
                            lineHeight: '1.2'
                          }}>
                            {formatName(item.sub_category?.[0]) || formatName(item.category)}
                          </p>
                        </div>
                        <Row justify="space-between" align="middle" style={{ marginTop: 'auto' }}>
                          <div>
                            <h2 style={{
                              margin: 0,
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#1890ff',
                              lineHeight: '1.2'
                            }}>
                              ${price.toFixed(2)} MXN
                            </h2>
                          </div>
                          <Tooltip title="Agregar al carrito">
                            <Button
                              size="small"
                              icon={addingToCart[item.id] ? <Spin size="small" /> : <ShoppingCartOutlined />}
                              style={{
                                background: 'linear-gradient(135deg, #1890ff, #096dd9)',
                                color: "#fff",
                                border: "none",
                                flexShrink: 0,
                                borderRadius: 8,
                                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                              }}
                              onClick={(e) => addToCart(item, e)}
                              disabled={addingToCart[item.id]}
                              loading={addingToCart[item.id]}
                            />
                          </Tooltip>
                        </Row>
                      </div>
                    </Card>
                  ) : (
                    <Card
                      bordered={false}
                      style={{
                        borderRadius: 12,
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                        padding: '16px',
                        cursor: 'pointer',
                        width: '100%'
                      }}
                      hoverable
                      onClick={() => handleCardClick(item)}
                    >
                      <Row gutter={16} align="middle">
                        <Col xs={4}>
                          <img
                            src={mainImage}
                            alt={item.name}
                            style={{
                              width: "100%",
                              borderRadius: 8,
                              height: 80,
                              objectFit: 'contain'
                            }}
                          />
                        </Col>
                        <Col xs={14}>
                          <h3 style={{
                            margin: '0 0 6px 0',
                            fontSize: '16px',
                            fontWeight: '500'
                          }}>
                            {item.name}
                          </h3>
                          <p style={{
                            color: "#00C897",
                            margin: 0,
                            fontWeight: "600",
                            fontSize: '14px'
                          }}>
                            {formatName(item.sub_category?.[0]) || formatName(item.category)}
                          </p>
                        </Col>
                        <Col xs={6}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <h2 style={{
                              margin: '0 0 8px 0',
                              fontSize: '18px',
                              fontWeight: '600',
                              color: '#1890ff'
                            }}>
                              ${price.toFixed(2)} MXN
                            </h2>
                            <Tooltip title="Agregar al carrito">
                              <Button
                                size="small"
                                icon={addingToCart[item.id] ? <Spin size="small" /> : <ShoppingCartOutlined />}
                                style={{
                                  background: 'linear-gradient(135deg, #1890ff, #096dd9)',
                                  color: "#fff",
                                  border: "none",
                                  fontSize: '12px',
                                  borderRadius: 8,
                                  boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                }}
                                onClick={(e) => addToCart(item, e)}
                                disabled={addingToCart[item.id]}
                                loading={addingToCart[item.id]}
                              >
                                {addingToCart[item.id] ? '' : 'Agregar'}
                              </Button>
                            </Tooltip>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  )}
                </List.Item>
              );
            }}
          />
        ) : (
          <Empty
            description="No se encontraron productos para los filtros seleccionados"
            style={{
              margin: '50px 0',
              padding: '40px'
            }}
          >
            <Button type="primary" onClick={() => history('/marketplace')}>
              Volver al Marketplace
            </Button>
          </Empty>
        )}
      </div>
    </LayoutPage>
  );
};

export default FilteredProducts;